import styled, { css } from 'styled-components'
import {
  PRIMARY_BLUE_02,
  PRIMARY_BLUE_03,
  GRAY_SCALE_04,
  SECONDARY_GREEN_07,
  SECONDARY_GREEN_10,
} from 'bd-web-components/src/utils/dash-colors'
import { getDashColor } from '../../styles'

/* ---------- Card de entrada (mesmo padrão do MailingDownloadButton) ---------- */

export const ContainerCard = styled.div`
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  height: 100%;
  @media (max-width: 1152px) {
    max-width: 100%;
  }
`

export const DetailCard = styled.div`
  display: flex;
  height: 100%;
  border: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  border-radius: 5px;
  padding: 0px 30px;
  margin: 8px;
  word-break: break-word;

  @media (max-width: 762px) {
    width: 100%;
    padding: 12px 20px;
  }
`

export const LabelCard = styled.div`
  color: ${GRAY_SCALE_04};
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  padding: 0 20px;
  align-content: center;
  width: 100%;
`

export const CardTitle = styled.div`
  color: ${PRIMARY_BLUE_02};
  font-size: 17px;
  font-weight: 700;
  line-height: 19.6px;
`

export const IconDetailContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  &:before {
    content: '';
    width: 38px;
    height: 38px;
    background-color: ${SECONDARY_GREEN_10};
    border-radius: 100px;
    position: absolute;
    z-index: 0;
  }
  & div {
    z-index: 1;
  }
`

/* ---------- Wizard ---------- */

export const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  /* O ModalShell não aplica padding no corpo; o wizard cuida do próprio. */
  padding: 16px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`

export const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  margin-bottom: 16px;
  flex-wrap: wrap;
`

export const Step = styled.div<{ active?: boolean; done?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${GRAY_SCALE_04};

  ${({ active }) =>
    active &&
    css`
      color: ${PRIMARY_BLUE_02};
      font-weight: 700;
    `}
`

/** No mobile só o rótulo da etapa ativa aparece — os outros viram só o número. */
export const StepLabel = styled.span<{ active?: boolean }>`
  display: ${({ active }) => (active ? 'inline' : 'none')};
  white-space: nowrap;

  @media (min-width: 768px) {
    display: inline;
  }
`

export const StepNumber = styled.span<{ active?: boolean; done?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 100px;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
  background-color: ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  color: ${GRAY_SCALE_04};

  ${({ active }) =>
    active &&
    css`
      background-color: ${SECONDARY_GREEN_07};
      color: #fff;
    `}

  ${({ done }) =>
    done &&
    css`
      background-color: ${SECONDARY_GREEN_10};
      color: ${SECONDARY_GREEN_07};
    `}
`

export const StepSeparator = styled.span`
  flex: 1;
  max-width: 48px;
  height: 2px;
  background-color: ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
`

export const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }
`

/** Agrupa os botões de ação em massa lado a lado mesmo no mobile. */
export const ToolbarButtons = styled.div`
  display: flex;
  gap: 10px;

  & > button {
    flex: 1;
  }

  @media (min-width: 768px) {
    & > button {
      flex: initial;
    }
  }
`

export const ToolbarHint = styled.span`
  font-size: 13px;
  color: ${GRAY_SCALE_04};
  margin-right: auto;
`

export const ScrollArea = styled.div`
  max-height: 46vh;
  overflow-y: auto;
  overflow-x: auto;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${GRAY_SCALE_04};
    padding: 10px 8px;
    border-bottom: 2px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
    white-space: nowrap;
  }

  td {
    padding: 8px;
    border-bottom: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
    vertical-align: middle;
    color: ${PRIMARY_BLUE_03};
  }
`

export const CouponName = styled.td`
  font-weight: 600;
  color: ${PRIMARY_BLUE_02} !important;
`

export const Input = styled.input<{ hasError?: boolean }>`
  border: 1px solid
    ${({ hasError }) =>
    hasError ? '#d64545' : getDashColor('GRAY_SCALE_09', 'BEIGE')};
  border-radius: 6px;
  padding: 9px;
  /* 16px evita o zoom automático do iOS ao focar o campo (webview do app). */
  font-size: 16px;
  width: 100%;
  min-width: 0;
  color: ${PRIMARY_BLUE_03};

  &:focus {
    outline: none;
    border-color: ${SECONDARY_GREEN_07};
  }

  @media (min-width: 768px) {
    font-size: 13px;
    padding: 7px 9px;
  }
`

export const CodeInput = styled(Input)`
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`

export const Select = styled.select`
  border: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  border-radius: 6px;
  padding: 9px;
  font-size: 13px;
  width: 100%;
  min-width: 0;
  color: ${PRIMARY_BLUE_03};
  background: transparent;

  @media (min-width: 768px) {
    width: auto;
    min-width: 240px;
    padding: 7px 9px;
  }
`

export const CodeCell = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`

const buttonBase = css`
  cursor: pointer;
  font-size: 13px;
  border-radius: 6px;
  padding: 9px 16px;
  border: 1px solid transparent;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

export const PrimaryButton = styled.button`
  ${buttonBase};
  background-color: ${SECONDARY_GREEN_07};
  color: #fff;
`

export const GhostButton = styled.button`
  ${buttonBase};
  background: transparent;
  border-color: ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  color: ${PRIMARY_BLUE_02};
`

export const SoftButton = styled.button`
  ${buttonBase};
  background-color: ${SECONDARY_GREEN_10};
  border-color: ${SECONDARY_GREEN_10};
  color: ${SECONDARY_GREEN_07};
  padding: 6px 10px;
  font-size: 12px;
  white-space: nowrap;
`

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${GRAY_SCALE_04};
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  padding: 4px;
`

export const NewCouponBox = styled.div`
  background-color: ${SECONDARY_GREEN_10};
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  & > div {
    min-width: 0;
  }

  label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: ${SECONDARY_GREEN_07};
    margin-bottom: 6px;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;

    & > div {
      flex: 1;
      min-width: 180px;
    }
  }
`

export const Hint = styled.div`
  font-size: 12px;
  color: ${GRAY_SCALE_04};
  border: 1px dashed ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 14px;
`

export const ErrorMessage = styled.div`
  font-size: 13px;
  color: #d64545;
  margin-top: 12px;
`

/* ---------- Layout de cards (mobile) ---------- */

export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const CouponCard = styled.div`
  border: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const CouponCardTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${PRIMARY_BLUE_02};
  word-break: break-word;
`

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const FieldLabel = styled.label`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${GRAY_SCALE_04};
  font-weight: 600;
`

/** Duas colunas lado a lado dentro do card (ex.: % e R$, limite e validade). */
export const FieldPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`

export const CardMeta = styled.div`
  font-size: 12px;
  color: ${GRAY_SCALE_04};
`

/* ---------- Footer ---------- */

export const Footer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  gap: 10px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`

export const FooterActions = styled.div`
  display: flex;
  gap: 10px;

  & > button {
    flex: 1;
  }

  @media (min-width: 768px) {
    & > button {
      flex: initial;
    }
  }
`

export const EmptyState = styled.div`
  text-align: center;
  padding: 26px;
  color: ${GRAY_SCALE_04};
  font-size: 13px;
`

/**
 * O <Loader> padrão ocupa 100vh (feito p/ tela cheia). Dentro da modal isso
 * estoura a altura no primeiro carregamento — aqui centralizamos um loader
 * enxuto numa área de altura fixa.
 */
export const LoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
`

/* ---------- Etapa 3 ---------- */

export const SuccessBox = styled.div`
  text-align: center;
  padding: 10px 0 0;
`

export const SuccessTitle = styled.h3`
  margin: 12px 0 6px;
  color: ${PRIMARY_BLUE_02};
`

export const SuccessText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${GRAY_SCALE_04};
`

export const SuccessSummary = styled.div`
  display: inline-block;
  text-align: left;
  border: 1px solid ${getDashColor('GRAY_SCALE_09', 'BEIGE')};
  border-radius: 8px;
  padding: 14px 18px;
  margin-top: 14px;
  font-size: 13px;
  color: ${PRIMARY_BLUE_03};
`

export const CodeTag = styled.td`
  font-weight: 700;
  letter-spacing: 1px;
  color: ${SECONDARY_GREEN_07} !important;
`
